import { useCallback, useEffect, useRef, useState } from "react";
import {
  getSiteWatch,
  listOpenIncidents,
  refreshSiteWatch,
  saveSiteWatch,
  type IncidentView,
  type SiteWatchSettings,
  type SiteWatchState,
} from "../../../../lib/siteWatch";

/**
 * The Rikked tab's state: the lead watch's settings and stats, the open
 * incidents, and the two things an operator can do (change a setting, rebuild
 * the list). `WatchPanel` draws it as the engine strip, with its settings
 * panel, over the incident card.
 *
 * The strip and the incident card read separately and fail separately, so a
 * failed read never throws: it becomes a flag the tab turns into a sentence,
 * and the incidents still draw when the watch's own read failed. While the
 * watch is on and the tab is visible the hook re-reads once a minute, the
 * watch's own tick, so the "last check" and network lines do not go stale on
 * an open page.
 */

/** Time after a list refresh before the second re-read, so the stats settle. */
const REFRESH_SETTLE_MS = 1500;
/** The watch ticks once a minute; reading more often shows nothing new. */
const ENABLED_POLL_MS = 60_000;

export type WatchNotice =
  | { kind: "saveFailed" }
  | { kind: "refreshFailed" }
  | { kind: "refreshed"; watched: number };

export function useSiteWatch() {
  const [state, setState] = useState<SiteWatchState | null>(null);
  const [incidents, setIncidents] = useState<IncidentView[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [incidentsFailed, setIncidentsFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState<WatchNotice | null>(null);

  const requestId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    const [watch, open] = await Promise.allSettled([getSiteWatch(), listOpenIncidents()]);
    // A newer read started while this one was out: its answer wins.
    if (!alive.current || id !== requestId.current) return;
    if (watch.status === "fulfilled") {
      setState(watch.value);
      setLoadFailed(false);
    } else {
      setLoadFailed(true);
    }
    if (open.status === "fulfilled") {
      setIncidents(open.value.incidents ?? []);
      setIncidentsFailed(false);
    } else {
      setIncidentsFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const enabled = Boolean(state?.settings.enabled);
  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") void load();
    }, ENABLED_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  /**
   * Saves a change and re-reads. Resolves true once saved, so a field can put
   * the stored value back after a refusal: the re-read alone brings the same
   * number and changes nothing a field would notice.
   */
  const patch = useCallback(
    async (change: Partial<SiteWatchSettings>): Promise<boolean> => {
      setSaving(true);
      setNotice(null);
      let saved = false;
      try {
        const res = await saveSiteWatch(change);
        saved = true;
        if (alive.current) setState((s) => (s ? { ...s, settings: res.settings } : s));
      } catch {
        if (alive.current) setNotice({ kind: "saveFailed" });
      } finally {
        if (alive.current) setSaving(false);
      }
      // Re-read either way: a refused save must show the stored value, not the typed one.
      await load();
      return saved;
    },
    [load],
  );

  const refreshList = useCallback(async () => {
    setRefreshing(true);
    setNotice(null);
    try {
      const res = await refreshSiteWatch();
      if (!alive.current) return;
      setNotice({ kind: "refreshed", watched: res.watched });
      await load();
      // The POST answers once the list is written, but the due count and the
      // incident figures are read in the same moment; one delayed re-read lets
      // them catch up instead of leaving the old numbers on the card.
      await new Promise((resolve) => setTimeout(resolve, REFRESH_SETTLE_MS));
      await load();
    } catch {
      if (alive.current) setNotice({ kind: "refreshFailed" });
    } finally {
      if (alive.current) setRefreshing(false);
    }
  }, [load]);

  return {
    state,
    incidents,
    loadFailed,
    incidentsFailed,
    saving,
    refreshing,
    notice,
    reload: load,
    patch,
    refreshList,
  };
}
